<template>
  <div class="gallery-page white-theme">
    <div class="container">
      <div class="page-header text-center">
        <span class="section-tag">{{ $t('gallery.tag') }}</span>
        <h1 class="section-title">{{ $t('gallery.title') }}</h1>
        <p class="section-subtitle">{{ $t('gallery.subtitle') }}</p>
      </div>
      <div class="gallery-filters">
        <button v-for="f in filters" :key="f.key" :class="['filter-btn', { active: activeFilter === f.key }]" @click="setFilter(f.key)">
          {{ $t(`gallery.filters.${f.key}`) }}
        </button>
      </div>
      <div v-if="loading" class="gallery-loading text-center">
        <div class="loading-spinner-large"></div>
        <p>{{ $t('common.loading') }}</p>
      </div>
      <div v-else-if="!isLoggedIn" class="gallery-empty text-center">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin-bottom: 16px; opacity: 0.5;"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        <p style="font-size: 16px; margin-bottom: 16px;">{{ $t('gallery.loginRequired') }}</p>
        <button class="btn btn-primary" @click="goLogin">{{ $t('nav.login') }}</button>
      </div>
      <div v-else-if="historyList.length === 0" class="gallery-empty text-center">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin-bottom: 16px; opacity: 0.5;"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
        <p style="font-size: 16px;">{{ $t('gallery.empty') }}</p>
      </div>
      <div v-else class="gallery-grid">
        <div v-for="item in historyList" :key="item.historyId || item.id || item.taskId" class="gallery-item" @click="openPreview(item)">
          <div class="item-media">
            <div v-if="isMusic(item)" class="item-music-cover-wrap">
              <img :src="getMusicCoverByIndex(item, 0)" :alt="item.aiDesc" loading="lazy" />
            </div>
            <img v-else-if="isImage(item.aiResultUrl) || isLocalCachedImage(item)" :src="proxiedMediaUrl(normalizeResultUrl(getDisplayUrl(item)))" :alt="item.aiDesc" loading="lazy" />
            <video v-else-if="isVideo(item.aiResultUrl) || isLocalCachedVideo(item)" :src="proxiedMediaUrl(normalizeResultUrl(getDisplayUrl(item)))" :poster="getVideoPoster(item.aiResultUrl)" preload="metadata" muted></video>
            <div v-else class="item-placeholder">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
            </div>
            <div class="item-overlay">
              <span class="item-model">{{ getModelLabel(item.aiName) }}</span>
              <span class="item-status" :class="getStatusClass(item.state)">{{ getStatusLabel(item.state) }}</span>
            </div>
          </div>
          <div class="item-info">
            <p class="item-prompt">{{ truncate(item.aiDesc, 80) }}</p>
            <span class="item-date">{{ formatDate(item.createDate) }}</span>
          </div>
        </div>
      </div>
      <div v-if="hasMore && !loading" class="gallery-load-more text-center">
        <button class="btn btn-secondary" @click="loadMore">{{ $t('gallery.loadMore') }}</button>
      </div>
    </div>

    <div class="modal-overlay" v-if="previewItem" @click.self="closePreview">
      <div class="preview-modal">
        <button class="modal-close" @click="closePreview">&times;</button>
        <div class="preview-content">
          <div v-if="isMusic(previewItem)" class="preview-music-wrap">
            <img :src="getMusicCoverByIndex(previewItem, musicTrackIndex)" :alt="previewItem.aiDesc" class="preview-music-cover" />
            <button class="preview-music-play-btn" @click.stop="togglePreviewMusic">
              <svg v-if="!musicIsPlaying" width="46" height="46" viewBox="0 0 24 24" fill="currentColor"><polygon points="8,5 19,12 8,19"/></svg>
              <svg v-else width="46" height="46" viewBox="0 0 24 24" fill="currentColor"><rect x="7" y="6" width="4" height="12"/><rect x="13" y="6" width="4" height="12"/></svg>
            </button>
            <audio ref="previewMusicAudio" :src="getMusicAudioByIndex(previewItem, musicTrackIndex)" @ended="musicIsPlaying = false"></audio>
          </div>
          <template v-else>
            <video v-if="isLocalCachedVideo(previewItem)" :src="previewCurrentUrl" controls autoplay :key="previewCurrentUrl"></video>
            <img v-else-if="isLocalCachedImage(previewItem)" :src="previewCurrentUrl" :alt="previewItem.aiDesc" />
            <img v-else-if="isImage(previewCurrentUrl)" :src="proxiedMediaUrl(previewCurrentUrl)" :alt="previewItem.aiDesc" />
            <video v-else-if="isVideo(previewCurrentUrl)" :src="proxiedMediaUrl(previewCurrentUrl)" controls autoplay :key="previewCurrentUrl"></video>
          </template>
          <button v-if="previewHasMultiple && previewIndex > 0" class="preview-arrow preview-arrow-left" @click.stop="prevPreview">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button v-if="previewHasMultiple && previewIndex < previewTotal - 1" class="preview-arrow preview-arrow-right" @click.stop="nextPreview">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg>
          </button>
          <div v-if="previewHasMultiple" class="preview-indicator">{{ previewIndex + 1 }} / {{ previewTotal }}</div>
        </div>
        <div class="preview-info">
          <div class="preview-music-track-tabs preview-music-track-tabs-inline" v-if="isMusic(previewItem) && getMusicTrackCount(previewItem) > 1">
            <button
              v-for="trackNum in getMusicTrackCount(previewItem)"
              :key="trackNum"
              type="button"
              :class="['preview-track-tab', { active: musicTrackIndex === trackNum - 1 }]"
              @click="switchMusicTrack(trackNum - 1)">
              音轨{{ trackNum }}
            </button>
          </div>
          <div class="preview-header">
            <span class="preview-model">{{ getModelLabel(previewItem.aiName) }}</span>
            <span class="preview-date">{{ formatDate(previewItem.createDate) }}</span>
          </div>
          <p class="preview-prompt">{{ previewItem.aiDesc }}</p>
          <p v-if="isVideo(getPreviewDownloadUrl(previewItem)) || isLocalCachedVideo(previewItem)" class="preview-download-hint">请使用播放器右下角下载按钮保存视频</p>
          <div class="preview-actions">
            <button v-if="!isVideo(getPreviewDownloadUrl(previewItem)) && !isLocalCachedVideo(previewItem)" class="btn btn-secondary btn-sm" @click="downloadResult">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              {{ $t('gallery.download') }}
            </button>
            <button class="btn btn-secondary btn-sm" @click="usePrompt">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              {{ $t('gallery.usePrompt') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAIHistory } from '@/api/aiHistory'
import { isAuthenticated } from '@/api/auth'
import { batchGetLocalMediaMaps, getMediaCoverObjectUrlList } from '@/utils/mediaCache'
import { proxifyVolcArkTosFetchUrl } from '@/api/ai/official/volc-ark-tos-fetch-url'

export default {
  name: 'Gallery',
  data() {
    return {
      activeFilter: 'all',
      filters: [
        { key: 'all' },
        { key: 'pending' },
        { key: 'finish' },
        { key: 'failed' }
      ],
      historyList: [],
      loading: false,
      pageNumber: 1,
      pageSize: 20,
      totalPage: 0,
      previewItem: null,
      previewIndex: 0,
      musicIsPlaying: false,
      musicTrackIndex: 0,
      localMediaMap: {},
      localMultiMediaMap: {},
      /** Suno 等：image_large_url 为空时，封面来自 IndexedDB（saveMediaCoverBlobList） */
      localMusicCoverMap: {}
    }
  },
  computed: {
    userInfo() {
      return this.$store.state.userInfo
    },
    isLoggedIn() {
      return !!this.$store.state.userInfo
    },
    hasMore() {
      return this.pageNumber < this.totalPage - 1
    },
    previewUrls() {
      if (!this.previewItem) return []
      var remote = this.previewItem.aiResultUrl
      if (remote && String(remote).trim()) return this.normalizeUrlList(remote)
      var localList = this.getLocalMediaUrlList(this.previewItem)
      return localList.length ? localList : []
    },
    previewCurrentUrl() {
      return this.previewUrls[this.previewIndex] || ''
    },
    previewTotal() {
      return this.previewUrls.length
    },
    previewHasMultiple() {
      return this.previewTotal > 1 && !this.isMusic(this.previewItem)
    }
  },
  mounted() {
    if (this.isLoggedIn) {
      this.loadHistory()
    }
  },
  watch: {
    isLoggedIn: function(val) {
      if (val && this.historyList.length === 0 && !this.loading) {
        this.loadHistory()
      }
    }
  },
  methods: {
    goLogin() {
      this.$store.dispatch('showLoginModal')
    },
    loadHistory() {
      var self = this
      self.loading = true
      var currentFilter = self.activeFilter
      var state = 0
      if (currentFilter === 'pending') {
        state = 1
      } else if (currentFilter === 'finish') {
        state = 2
      } else if (currentFilter === 'failed') {
        state = 3
      }
      var params = {
        pageNumber: self.pageNumber,
        pageSize: self.pageSize,
        state: state
      }
      getAIHistory(params).then(function(res) {
        if (self.activeFilter !== currentFilter) {
          return
        }
        if (res && res.historyList) {
          if (self.pageNumber === 1) {
            self.historyList = res.historyList
          } else {
            self.historyList = self.historyList.concat(res.historyList)
          }
          self.totalPage = res.totalPage || 1
          self.loadLocalMediaCache(res.historyList)
          self.loadLocalMusicCoverCache(res.historyList)
        }
      }).catch(function(err) {
        console.error('Failed to load history:', err)
      }).finally(function() {
        if (self.activeFilter === currentFilter) {
          self.loading = false
        }
      })
    },
    loadLocalMediaCache: function(items) {
      var self = this
      var ids = []
      var seen = {}
      items.forEach(function (item) {
        if (!item) return
        // 只对 aiResultUrl 为空的记录查本地缓存（Hailuo 等本地缓存模式）
        if (item.aiResultUrl && item.aiResultUrl.trim() !== '') return

        var historyKeyVal = item.historyId != null ? item.historyId : item.id
        if (historyKeyVal != null) {
          var idKey = String(historyKeyVal)
          if (!seen[idKey]) {
            seen[idKey] = true
            ids.push(historyKeyVal)
          }
        }
        if (item.taskId) {
          var taskKey = String(item.taskId)
          if (!seen[taskKey]) {
            seen[taskKey] = true
            ids.push(item.taskId)
          }
        }
      })
      if (!ids.length) return
      batchGetLocalMediaMaps(ids).then(function (res) {
        self.localMediaMap = Object.assign({}, self.localMediaMap, res.single)
        self.localMultiMediaMap = Object.assign({}, self.localMultiMediaMap, res.multi)
      }).catch(function (e) {
        console.warn('[Gallery] loadLocalMediaCache failed', e)
      })
    },
    /** 音乐且远端无封面 URL 时，按 historyId / taskId 拉本地封面 object URL（与 Music.vue 写入键一致） */
    loadLocalMusicCoverCache: function (items) {
      var self = this
      if (!items || !items.length) return
      var ids = []
      var seen = {}
      items.forEach(function (item) {
        if (!item) return
        if (!self.isMusic(item)) return
        if (self.normalizeUrlList(item.image_large_url).length) return
        var hk = item.historyId != null ? item.historyId : item.id
        if (hk != null) {
          var ks = String(hk)
          if (!seen[ks]) {
            seen[ks] = true
            ids.push(hk)
          }
        }
        if (item.taskId) {
          var ts = String(item.taskId)
          if (!seen[ts]) {
            seen[ts] = true
            ids.push(item.taskId)
          }
        }
      })
      if (!ids.length) return
      var tasks = ids.map(function (id) {
        return getMediaCoverObjectUrlList(id).then(function (urls) {
          return { id: id, urls: urls && urls.length ? urls : null }
        })
      })
      Promise.all(tasks).then(function (results) {
        var next = Object.assign({}, self.localMusicCoverMap)
        for (var i = 0; i < results.length; i++) {
          var r = results[i]
          if (r && r.urls && r.urls.length) {
            next[String(r.id)] = r.urls
          }
        }
        self.localMusicCoverMap = next
      }).catch(function (e) {
        console.warn('[Gallery] loadLocalMusicCoverCache failed', e)
      })
    },
    getLocalMusicCoverUrlList: function (item) {
      if (!item) return []
      var map = this.localMusicCoverMap || {}
      var hk = item.historyId != null ? item.historyId : item.id
      if (hk != null) {
        var a = map[String(hk)]
        if (a && a.length) return a.slice()
      }
      if (item.taskId) {
        var b = map[String(item.taskId)]
        if (b && b.length) return b.slice()
      }
      return []
    },
    getLocalMediaUrlList: function(item) {
      if (!item) return []
      var historyKeyVal = item.historyId != null ? item.historyId : item.id
      if (historyKeyVal != null) {
        var multi = this.localMultiMediaMap[historyKeyVal]
        if (multi && multi.length) return multi.slice()
        var one = this.localMediaMap[historyKeyVal]
        if (one) return [one]
      }
      if (item.taskId) {
        var multiT = this.localMultiMediaMap[item.taskId]
        if (multiT && multiT.length) return multiT.slice()
        var oneT = this.localMediaMap[item.taskId]
        if (oneT) return [oneT]
      }
      return []
    },
    getDisplayUrl: function(item) {
      if (!item) return ''
      var remote = String(item.aiResultUrl || '').trim()
      if (remote) return remote
      var list = this.getLocalMediaUrlList(item)
      if (list.length) return list[0]
      return ''
    },
    hasLocalMedia: function(item) {
      return this.getLocalMediaUrlList(item).length > 0
    },
    /** 无 aiResultUrl 时本地缓存可能是图或视频：用作品模型名与 store 中模型列表对齐 */
    getHistoryItemMediaCategory: function(item) {
      if (!item || this.isMusic(item)) return 'music'
      var n = String(item.aiName || '').trim().toLowerCase()
      if (!n) return null
      var imageModels = this.$store.state.imageModels || []
      var i
      for (i = 0; i < imageModels.length; i++) {
        var im = imageModels[i]
        if (String(im.id || '').toLowerCase() === n) return 'image'
        if (String(im.name || '').toLowerCase() === n) return 'image'
      }
      var videoModels = this.$store.state.videoModels || []
      for (i = 0; i < videoModels.length; i++) {
        var vm = videoModels[i]
        if (String(vm.id || '').toLowerCase() === n) return 'video'
        if (String(vm.name || '').toLowerCase() === n) return 'video'
        if (vm.navName && String(vm.navName).toLowerCase() === n) return 'video'
      }
      return null
    },
    isLocalCachedImage: function(item) {
      if (!this.hasLocalMedia(item) || this.isMusic(item)) return false
      return this.getHistoryItemMediaCategory(item) !== 'video'
    },
    isLocalCachedVideo: function(item) {
      if (!this.hasLocalMedia(item) || this.isMusic(item)) return false
      return this.getHistoryItemMediaCategory(item) === 'video'
    },
    getPreviewDownloadUrl: function(item) {
      if (!item) return ''
      if (item.aiResultUrl) return item.aiResultUrl
      var list = this.getLocalMediaUrlList(item)
      return list.length ? list[0] : ''
    },
    setFilter(key) {
      this.activeFilter = key
      this.pageNumber = 1
      this.historyList = []
      this.loadHistory()
    },
    loadMore() {
      this.pageNumber++
      this.loadHistory()
    },
    /** 与 GenerationHistory 一致：本地开发走 vue 代理，避免 Pollo CDN / 火山 TOS 直连无法播放 */
    proxiedMediaUrl: function (url) {
      var s = String(url || '').trim()
      if (!s) return ''
      if (s.indexOf('blob:') === 0 || s.indexOf('data:') === 0) return s
      return proxifyVolcArkTosFetchUrl(s)
    },
    normalizeResultUrl(url) {
      if (!url) return ''
      return String(url).split(',')[0].trim()
    },
    normalizeUrlList(url) {
      if (!url) return []
      return String(url).split(',').map(function(v) { return v.trim() }).filter(Boolean)
    },
    isAudio(url) {
      var normalized = this.normalizeResultUrl(url)
      if (!normalized) return false
      var ext = normalized.split('?')[0].toLowerCase()
      return ext.match(/\.(mp3|wav|m4a|aac|ogg)$/) !== null
    },
    isMusic(item) {
      if (!item) return false
      return String(item.aiName || '').toLowerCase() === 'ai music' || this.isAudio(item.aiResultUrl)
    },
    getMusicTrackCount(item) {
      var localList = this.getLocalMediaUrlList(item)
      if (localList.length) return localList.length
      return this.normalizeUrlList(item && item.aiResultUrl).length
    },
    getMusicAudioByIndex(item, index) {
      var localList = this.getLocalMediaUrlList(item)
      if (localList.length) {
        var u = localList[index] || localList[0]
        if (u) return u
      }
      var urls = this.normalizeUrlList(item && item.aiResultUrl)
      if (!urls.length) return ''
      return urls[index] || urls[0]
    },
    getMusicCoverByIndex(item, index) {
      var covers = this.normalizeUrlList(item && item.image_large_url)
      if (covers.length) {
        return covers[index] || covers[0]
      }
      var localCovers = this.getLocalMusicCoverUrlList(item)
      if (localCovers.length) {
        return localCovers[index] || localCovers[0]
      }
      return ''
    },
    isImage(url) {
      var normalized = this.normalizeResultUrl(url)
      if (!normalized) return false
      var ext = normalized.split('?')[0].toLowerCase()
      if (ext.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/)) return true
      // AllAll Midjourney image endpoint has no extension, e.g. /mj/image/1774260775879252
      return /\/\/api\.allall\.ai\/mj\/image\/[^/?#]+/i.test(normalized)
    },
    isVideo(url) {
      var normalized = this.normalizeResultUrl(url)
      if (!normalized) return false
      var ext = normalized.split('?')[0].toLowerCase()
      return ext.match(/\.(mp4|webm|mov|avi|mkv)$/) !== null
    },
    getVideoPoster(url) {
      return null
    },
    getModelLabel(aiName) {
      var modelMap = {
        'midjourney': 'Midjourney',
        'gpt-image-1': 'GPT-Image-1',
        'seedream-5': 'Seedream 5',
        'nano-banana': 'Nano Banana', // 暂时隐藏
        'wan-2-5': 'Wan 2.5',
        'wan-2-6': 'Wan 2.6',
        'wan-2-2': 'Wan 2.2',
        'veo-3-0': 'Veo 3.0',
        'veo-3-1': 'Veo 3.1',
        'kling-2-5': 'Kling 2.5',
        'kling-2-6': 'Kling 2.6',
        'sora-2': 'Sora 2',
        'hailuo-2-3': 'Hailuo 2.3',
        'vidu-q3-pro': 'Vidu Q3 Pro',
        'seedance': 'Seedance',
        'runway': 'Runway',
        'wan-animate': 'Wan Animate'
      }
      return modelMap[aiName] || aiName || 'Unknown'
    },
    getStatusLabel(state) {
      var map = {
        1: this.$t('gallery.status.pending'),
        2: this.$t('gallery.status.finish'),
        3: this.$t('gallery.status.failed')
      }
      return map[state] || ''
    },
    getStatusClass(state) {
      var map = {
        1: 'status-pending',
        2: 'status-finish',
        3: 'status-failed'
      }
      return map[state] || ''
    },
    truncate(str, len) {
      if (!str) return ''
      return str.length > len ? str.substring(0, len) + '...' : str
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      var d = new Date(dateStr)
      var y = d.getFullYear()
      var m = String(d.getMonth() + 1).padStart(2, '0')
      var day = String(d.getDate()).padStart(2, '0')
      var h = String(d.getHours()).padStart(2, '0')
      var min = String(d.getMinutes()).padStart(2, '0')
      return y + '-' + m + '-' + day + ' ' + h + ':' + min
    },
    prevPreview() {
      if (this.previewIndex > 0) this.previewIndex--
    },
    nextPreview() {
      if (this.previewIndex < this.previewTotal - 1) this.previewIndex++
    },
    openPreview(item) {
      this.pausePreviewMusic()
      this.previewIndex = 0
      this.musicTrackIndex = 0
      this.previewItem = item
      if (this.isMusic(item)) {
        var self = this
        this.$nextTick(function() {
          self.togglePreviewMusic()
        })
      }
    },
    closePreview() {
      this.pausePreviewMusic()
      this.previewItem = null
    },
    pausePreviewMusic() {
      var audio = this.$refs.previewMusicAudio
      if (audio && typeof audio.pause === 'function') audio.pause()
      this.musicIsPlaying = false
    },
    switchMusicTrack(index) {
      this.musicTrackIndex = index
      this.musicIsPlaying = false
      var self = this
      this.$nextTick(function() {
        var audio = self.$refs.previewMusicAudio
        if (!audio || !audio.src) return
        var p = audio.play()
        self.musicIsPlaying = true
        if (p && typeof p.catch === 'function') {
          p.catch(function() { self.musicIsPlaying = false })
        }
      })
    },
    togglePreviewMusic() {
      var audio = this.$refs.previewMusicAudio
      if (!audio || !audio.src) return
      if (this.musicIsPlaying) {
        audio.pause()
        this.musicIsPlaying = false
        return
      }
      var p = audio.play()
      this.musicIsPlaying = true
      if (p && typeof p.catch === 'function') {
        var self = this
        p.catch(function() { self.musicIsPlaying = false })
      }
    },
    downloadResult() {
      if (!this.previewItem) return
      var displayUrl = this.previewCurrentUrl || this.getDisplayUrl(this.previewItem)
      if (!displayUrl) return

      if (this.isVideo(this.previewItem.aiResultUrl) || this.isLocalCachedVideo(this.previewItem)) {
        var videoUrl = this.normalizeResultUrl(displayUrl)
        if (!videoUrl) return
        var videoExt = this.getMediaExtension(this.previewItem.aiResultUrl || videoUrl, 'mp4')
        var videoBase = String((this.previewItem.aiName || 'video') + '_' + (this.previewItem.historyId || this.previewItem.taskId || 'media')).replace(/[/\\?%*:|"<>]/g, '-')
        this.downloadVideoWithFallback(videoUrl, videoBase + '.' + videoExt)
        return
      }
      if (this.isMusic(this.previewItem)) {
        var trackUrl = this.getMusicAudioByIndex(this.previewItem, this.musicTrackIndex)
        if (!trackUrl) return
        var fileBase = (this.previewItem.aiName || 'music') + '_' + (this.previewItem.taskId || 'track') + '_track' + (this.musicTrackIndex + 1)
        var filename = String(fileBase).replace(/[/\\?%*:|"<>]/g, '-') + '.mp3'
        this.downloadMediaFile(trackUrl, filename)
        return
      }
      var mediaUrl = this.normalizeResultUrl(this.previewItem.aiResultUrl)
      if (!mediaUrl && this.hasLocalMedia(this.previewItem) && !this.isLocalCachedVideo(this.previewItem)) {
        mediaUrl = this.normalizeResultUrl(displayUrl)
      }
      if (!mediaUrl) return
      var ext = this.getMediaExtension(mediaUrl, this.isVideo(this.previewItem.aiResultUrl) ? 'mp4' : 'jpg')
      var base = String((this.previewItem.aiName || 'result') + '_' + (this.previewItem.taskId || 'media')).replace(/[/\\?%*:|"<>]/g, '-')
      this.downloadMediaFile(mediaUrl, base + '.' + ext)
    },
    downloadVideoWithFallback(url, filename) {
      var self = this
      if (!url) return
      var urlStr = String(url)
      try {
        var nativeA = document.createElement('a')
        nativeA.href = urlStr
        nativeA.download = filename
        nativeA.rel = 'noopener'
        document.body.appendChild(nativeA)
        nativeA.click()
        document.body.removeChild(nativeA)
      } catch (e) {}
      fetch(urlStr, { mode: 'cors', credentials: 'omit' })
        .then(function(res) {
          return res.ok ? res.blob() : Promise.reject(new Error('HTTP ' + res.status))
        })
        .then(function(blob) {
          if (!blob || !blob.size) return Promise.reject(new Error('empty blob'))
          var blobUrl = URL.createObjectURL(blob)
          var a = document.createElement('a')
          a.href = blobUrl
          a.download = filename
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          setTimeout(function() { URL.revokeObjectURL(blobUrl) }, 2000)
        })
        .catch(function() {
          if (urlStr.indexOf('http') === 0) {
            try {
              window.open(urlStr, '_blank', 'noopener,noreferrer')
            } catch (eOpen) {}
          }
          if (self.$store && self.$store.dispatch) {
            self.$store.dispatch('showErrorModal', {
              title: self.$t ? self.$t('common.error') : 'Error',
              message: '当前视频源限制下载，请使用播放器右下角下载按钮，或在新标签页右键视频另存为'
            })
          }
        })
    },
    downloadMediaFile(url, filename) {
      var self = this
      fetch(url, { mode: 'cors' })
        .then(function(res) { return res.ok ? res.blob() : Promise.reject(new Error('download failed')) })
        .then(function(blob) {
          var blobUrl = URL.createObjectURL(blob)
          var a = document.createElement('a')
          a.href = blobUrl
          a.download = filename
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(blobUrl)
        })
        .catch(function() {
          try {
            var a = document.createElement('a')
            a.href = url
            a.download = filename
            a.rel = 'noopener'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
          } catch (e) {
            if (self.$store && self.$store.dispatch) {
              self.$store.dispatch('showErrorModal', {
                title: self.$t ? self.$t('common.error') : 'Error',
                message: '下载失败，请重试或联系客服'
              })
            }
          }
        })
    },
    getMediaExtension(url, fallbackExt) {
      var clean = String(url || '').split('?')[0]
      var m = clean.match(/\.([a-zA-Z0-9]+)$/)
      if (m && m[1]) return m[1].toLowerCase()
      return fallbackExt || 'bin'
    },
    usePrompt() {
      if (!this.previewItem || !this.previewItem.aiDesc) return
      var rawName = String(this.previewItem.aiName || '').trim()
      var modelId = rawName.toLowerCase()
      if (this.isMusic(this.previewItem)) {
        this.$router.push({
          path: '/music',
          query: {
            lyrics: this.previewItem.aiDesc,
            prompt: this.previewItem.aiDesc
          }
        })
        return
      }
      var routeMap = {
        'midjourney': '/models/midjourney',
        'midjourney v7': '/models/midjourney',
        'gpt-image-1': '/models/gpt-image-1',
        'seedream-5': '/models/seedream-5',
        'seedream 5': '/models/seedream-5',
        // 'nano-banana': '/models/nano-banana', // 暂时隐藏
        // 'nano banana': '/models/nano-banana', // 暂时隐藏
        'wan-2-5': '/models/wan-2-5',
        'wan 2.5': '/models/wan-2-5',
        'wan-2-6': '/models/wan-2-6',
        'wan 2.6': '/models/wan-2-6',
        'wan-2-2': '/models/wan-2-2',
        'wan 2.2': '/models/wan-2-2',
        'veo-3-0': '/models/veo-3-0',
        'veo 3.0': '/models/veo-3-0',
        'veo-3-1': '/models/veo-3-1',
        'veo 3.1': '/models/veo-3-1',
        'veo 3.1 fast': '/models/veo-3-1-fast',
        'veo-3-1-fast': '/models/veo-3-1-fast',
        'kling-2-5': '/models/kling-2-5',
        'kling 2.5': '/models/kling-2-5',
        'kling-2-6': '/models/kling-2-6',
        'kling 2.6': '/models/kling-2-6',
        'sora-2': '/models/sora-2',
        'sora 2': '/models/sora-2',
        'hailuo-2-3': '/models/hailuo-2-3',
        'hailuo 2.3': '/models/hailuo-2-3',
        'vidu-q3-pro': '/models/vidu-q3-pro',
        'vidu q3 pro': '/models/vidu-q3-pro',
        'seedance': '/models/seedance-1-5',
        'seedance 1.0': '/models/seedance-1-5',
        'seedance-1-5': '/models/seedance-1-5',
        'seedance 1.5 pro': '/models/seedance-1-5',
        'runway': '/models/runway',
        'wan-animate': '/models/wan-animate',
        'wan animate': '/models/wan-animate'
      }
      var route = routeMap[modelId] || '/models/seedream-4'
      this.$router.push({ path: route, query: { prompt: this.previewItem.aiDesc } })
    }
  }
}
</script>

<style scoped>
.gallery-page { padding: 48px 0 80px; }
.page-header { margin-bottom: 32px; }
.section-tag {
  display: inline-block;
  padding: 6px 16px;
  background: var(--accent-light);
  border: 1px solid var(--accent-border);
  border-radius: var(--radius-full);
  font-size: 12px;
  color: var(--accent);
  font-weight: 600;
  margin-bottom: 16px;
}
.section-title {
  font-size: 36px;
  font-weight: 800;
  margin-bottom: 8px;
}
.section-subtitle { color: var(--text-secondary); font-size: 16px; }

.gallery-filters { display: flex; gap: 8px; margin-bottom: 32px; flex-wrap: wrap; justify-content: center; }
.filter-btn { padding: 8px 20px; border-radius: var(--radius-full); font-size: 13px; color: var(--text-secondary); border: 1px solid var(--border-color); transition: all 0.2s; cursor: pointer; background: transparent; }
.filter-btn:hover { border-color: var(--accent); color: var(--text-primary); }
.filter-btn.active { background: var(--accent); color: #fff; border-color: var(--accent); font-weight: 600; }

.gallery-loading { padding: 80px 20px; color: var(--text-muted); }
.loading-spinner-large {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;
}
@keyframes spin { to { transform: rotate(360deg); } }

.gallery-empty { padding: 80px 20px; color: var(--text-muted); }

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}
.gallery-item {
  background: var(--bg-card);
  border: 1px solid var(--bg-card-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;
}
.gallery-item:hover { border-color: var(--accent-border); transform: translateY(-2px); }

.item-media {
  position: relative;
  aspect-ratio: 1;
  background: var(--bg-primary);
  overflow: hidden;
}
.item-media img,
.item-media video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.item-music-cover-wrap {
  position: relative;
  width: 100%;
  height: 100%;
}
.item-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
}
.item-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: linear-gradient(to bottom, rgba(0,0,0,0.6), transparent);
  opacity: 0;
  transition: opacity 0.2s;
}
.gallery-item:hover .item-overlay { opacity: 1; }
.item-model {
  padding: 4px 10px;
  background: rgba(0,0,0,0.7);
  border-radius: var(--radius-sm);
  font-size: 11px;
  color: #fff;
}
.item-status {
  padding: 4px 10px;
  border-radius: var(--radius-sm);
  font-size: 11px;
  font-weight: 600;
}
.status-pending { background: #f59e0b; color: #000; }
.status-finish { background: var(--accent); color: #fff; }
.status-failed { background: #ef4444; color: #fff; }

.item-info { padding: 12px; }
.item-prompt {
  font-size: 13px;
  color: var(--text-secondary);
  margin-bottom: 8px;
  line-height: 1.4;
}
.item-date { font-size: 12px; color: var(--text-muted); }

.gallery-load-more { padding: 20px 0; }

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}
.preview-modal {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-xl);
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}
.modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  background: rgba(0,0,0,0.5);
  border: none;
  border-radius: 50%;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-close:hover { background: rgba(0,0,0,0.7); }

.preview-content {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  min-height: 300px;
  max-height: 60vh;
  overflow: hidden;
}
.preview-content img,
.preview-content video {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.preview-music-wrap {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.preview-music-track-tabs {
  display: flex;
  gap: 8px;
}
.preview-music-track-tabs-inline {
  justify-content: center;
  margin-bottom: 12px;
}
.preview-track-tab {
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.35);
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: 12px;
  cursor: pointer;
}
.preview-track-tab.active {
  background: var(--accent);
  border-color: var(--accent);
  color: #fff;
  font-weight: 600;
}
.preview-music-cover {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.preview-music-play-btn {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 78px;
  height: 78px;
  border-radius: 999px;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.preview-music-play-btn:hover {
  background: rgba(0, 0, 0, 0.7);
}

.preview-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 5;
  transition: background 0.2s;
}
.preview-arrow:hover { background: rgba(0, 0, 0, 0.8); }
.preview-arrow-left { left: 12px; }
.preview-arrow-right { right: 12px; }
.preview-indicator {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 14px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  z-index: 5;
}

.preview-info { padding: 20px; }
.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.preview-model {
  padding: 4px 12px;
  background: var(--accent-light);
  border-radius: var(--radius-sm);
  font-size: 12px;
  color: var(--accent);
  font-weight: 600;
}
.preview-date { font-size: 13px; color: var(--text-muted); }
.preview-prompt {
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 16px;
}
.preview-download-hint {
  font-size: 13px;
  color: var(--text-muted);
  margin-bottom: 12px;
}
.preview-actions { display: flex; gap: 12px; }
.btn-sm { padding: 8px 16px; font-size: 13px; }

@media (max-width: 640px) {
  .gallery-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
  .section-title { font-size: 28px; }
}
@media (max-width: 480px) {
  .gallery-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
  .section-title { font-size: 24px; }
}
</style>
